import { Box, Chip } from '@mui/material'
import { useContext, useState } from 'react'
import { ReportInput } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ReportingFilterCard } from 'src/screens/reporting/shared/filter-card/ReportingFilterCard'
import { ReportGoalGrid } from 'src/screens/reporting/shared/report-card-content/report-goal/ReportGoalGrid'
import { ReportMeasureGrid } from 'src/screens/reporting/shared/report-card-content/report-measure/ReportMeasureGrid'
import { ReportCard } from 'src/screens/reporting/shared/report-card/ReportCard'
import { ReportingSearchResultsSection } from 'src/screens/reporting/shared/search-results-section/ReportingSearchResultsSection'
import { ReportService } from 'src/service/axios/ReportService'
import { PermissionService } from 'src/service/security/PermissionService'
import { PROJECT } from 'src/shared/constants/constants'
import { REPORTING_DOWNLOAD, REPORTING_DOWNLOAD_TYPE } from 'src/shared/constants/reporting-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { S } from 'src/shared/styled/S'
import { saveFileAs } from 'src/shared/utils/BlobUtils'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { UserContext, useUserLocale } from 'src/user/UserContext'

export const KapReportingPage = () => {
  const { getMessage } = useMessageSource()
  const language = useUserLocale()
  const notificationService = useNotificationService()
  const [programDataCount, setProgramDataCount] = useState<number>()
  const [relatedMeasureDataCount, setRelatedMeasureDataCount] = useState<number>()
  const [reportInput, setReportInput] = useState<ReportInput>()

  const { user } = useContext(UserContext)
  const globalRoles = user.roles
  const canDownloadFinancialsReport = PermissionService.permissionToDownloadKapFinancialsReport(globalRoles)

  const onDownloadHandle = (reportType: REPORTING_DOWNLOAD_TYPE) => async () => {
    if (reportInput) {
      await ReportService.generateReport(reportInput, '/kap', reportType, language)
        .then((response) => {
          saveFileAs(response)
        })
        .catch(() => {
          notificationService.operationFailed()
        })
    }
  }

  return (
    <ScreenLayout title={getMessage(`label.reporting.${PROJECT.KAP}`)} hasSecondLevelNavigation={false}>
      <PageLayout>
        <ReportingFilterCard
          process={PROJECT.KAP}
          setProcessDataCount={setProgramDataCount}
          setRelatedMeasuresCount={setRelatedMeasureDataCount}
          setReportInput={setReportInput}
        />
        <Box marginTop={5}>
          <ReportingSearchResultsSection>
            {programDataCount !== undefined && relatedMeasureDataCount !== undefined && reportInput && (
              <>
                <ReportCard
                  reportName={getMessage('label.program.information')}
                  handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.PROGRAM_INFORMATION)}
                >
                  <Chip
                    label={`${programDataCount} ${getMessage('label.programs')}`}
                    color="secondary"
                    variant="filled"
                    sx={{ marginLeft: 2 }}
                  />
                  <Chip
                    label={`${relatedMeasureDataCount} ${getMessage('label.project.measures')}`}
                    color="secondary"
                    variant="filled"
                    sx={{ marginLeft: 2 }}
                  />
                </ReportCard>
                {canDownloadFinancialsReport && (
                  <ReportCard
                    reportName={getMessage('label.financials')}
                    handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.FINANCIALS)}
                  />
                )}
                <S.DataGrid.ReportGridContainer>
                  <ReportCard
                    reportGrid={
                      <ReportGoalGrid
                        reportInput={reportInput}
                        process={PROJECT.KAP}
                        handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.GOALS)}
                      />
                    }
                  />
                  <ReportCard
                    reportGrid={
                      <ReportMeasureGrid
                        reportInput={reportInput}
                        process={PROJECT.KAP}
                        handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.MEASURES)}
                      />
                    }
                  />
                </S.DataGrid.ReportGridContainer>
                <ReportCard
                  reportName={getMessage('label.features.report')}
                  handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.FEATURES)}
                />
                <ReportCard
                  reportName={getMessage('label.kpi.report')}
                  handleDownload={onDownloadHandle(REPORTING_DOWNLOAD.KPI)}
                />
              </>
            )}
          </ReportingSearchResultsSection>
        </Box>
      </PageLayout>
    </ScreenLayout>
  )
}
